import React, { memo, useEffect } from 'react';
import { TableEditBar, withConfirm } from 'components';
import { Button, ClickAwayListener } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import GetAppIcon from '@mui/icons-material/GetApp';
import { GridView } from './GridView';
import { TableView } from './TableView';
import { AbstractResultsProps } from './types';
import { useSelection } from '../../../../utils/use-selection';
import {
  ContextMenuState,
  isDir,
  isFile,
  Resource,
  transformResourceToFile,
} from '../../../../modules/resources';
import { useKeyboardKey } from '../../../../utils/use-keyboard-key';
import { ViewMode } from '../../../../modules/ui/types';
import { Dir } from '../../../../modules/dirs';
import { useDirsActions } from '../../../../modules/dirs/hooks';
import { useFilesActions } from '../../../../modules/files/hooks';
import { useNavigate } from '../../../../utils/router';
import useResourceContextMenu from '../../../../modules/resources/hooks/use-resource-context-menu';
import ContextMenuContainer from '../ContextMenu/ContextMenuContainer';
import { useBasketActions } from '../../../../modules/basket/hooks';

import { useTranslation } from 'utils/translation';
import { WithConfirmProps } from '../../../../components/Confirmation/withConfirm';
import { shallowEqual } from 'react-redux';
import { useFilePreview } from '../../../../modules/files/providers/FilePreviewProvider';
import ContextualView from './ContextualView';
import { useDrawerContext } from '../../DrawerContext';
import { useDirectoryPicker } from '../../../../components/DirectoryPicker/DirectoryPickerProvider';

interface ResultViewModeProps extends WithConfirmProps {
  viewMode: ViewMode;
  showResourcePath?: boolean;
}

type Props = AbstractResultsProps & ResultViewModeProps;

export const ResultsView = ({
  viewMode,
  resources,
  onClick,
  onDeletedMany,
  confirm,
  ...rest
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { deleteDir, downloadDir } = useDirsActions();
  const { addDirectory, addFile } = useBasketActions();
  const { deleteFile, download } = useFilesActions();
  const { launchPreview } = useFilePreview();
  const { setSelection } = useDrawerContext();
  const { selectDirectory } = useDirectoryPicker();
  const { moveFile } = useFilesActions();
  const { moveDir } = useDirsActions();
  const views = {
    [ViewMode.Table]: TableView,
    [ViewMode.Grid]: GridView,
    [ViewMode.Contextual]: ContextualView,
  };

  const View = views[viewMode];
  const { isKeyDown: isShiftDown } = useKeyboardKey('Shift');
  const selectionProps = useSelection<Resource>(
    resources,
    (item) => (i) => i.id === item.id && i.type === item.type,
  );
  const {
    openContextMenu,
    closeContextMenu,
    isOpen: isContextMenuOpen,
    x,
    y,
    resource,
  } = useResourceContextMenu();

  useEffect(() => {
    if (selectionProps.selectedItems.length > 0) {
      setSelection(null);
    }
  }, [selectionProps.selectedItems, setSelection]);

  const handleItemRightClick = (data: ContextMenuState) => {
    openContextMenu(data);
  };

  const handleWrapperClick = (event: React.MouseEvent) => {
    if (!(isShiftDown || event.ctrlKey)) {
      selectionProps.deselectAll();
    }
  };

  const handleAwayClick = () => {
    selectionProps.deselectAll();
  };

  const handleItemClick = (item: Resource, event: React.MouseEvent) => {
    if (isShiftDown || event.ctrlKey) {
      selectionProps.selectOne(item);
    } else {
      selectionProps.deselectAll();
      selectionProps.setLastSelected(item);
      onClick(item, event);
    }
  };

  const handleItemDoubleClick = (item: Resource, event: React.MouseEvent) => {
    if (event.ctrlKey || event.shiftKey) {
      return;
    }

    if (isDir(item)) {
      navigate(`/directory/${item.id}`);
    }

    if (isFile(item)) {
      launchPreview(transformResourceToFile(item));
    }
  };

  const handleDeleteMany = (resources: Resource[]) => {
    const dirs = resources.filter(isDir);
    dirs.map((dir: Dir) => deleteDir(dir));

    const files = resources.filter(isFile);
    files.map((file) => deleteFile(file));
  };

  const handleAddToBasketMany = (resources: Resource[]) => {
    const dirs = resources.filter(isDir);
    dirs.map((dir: Dir) =>
      addDirectory({
        dir,
      }),
    );

    const files = resources.filter(isFile);
    files.map((file) =>
      addFile({
        file,
      }),
    );
  };

  const handleDownloadMany = (resources: Resource[]) => {
    const dirs = resources.filter(isDir);
    dirs.map((dir: Dir) => downloadDir(dir));

    const files = resources.filter(isFile);
    files.map((file) => download(file));
  };

  const downloadSelected = () => {
    handleDownloadMany(selectionProps.selectedItems);
    selectionProps.deselectAll();
  };

  const addToBasketSelected = () => {
    handleAddToBasketMany(selectionProps.selectedItems);
    selectionProps.deselectAll();
  };

  const deleteSelected = () => {
    handleDeleteMany(selectionProps.selectedItems);
    selectionProps.deselectAll();

    if (onDeletedMany) {
      onDeletedMany();
    }
  };

  const handleDeleteManyWithConfirm = () => {
    confirm(deleteSelected)();
  };

  const handleMoveMany = async () => {
    const result = await selectDirectory();

    if (result.canceled) {
      return;
    }

    const { id } = result;

    selectionProps.selectedItems.forEach((resource) => {
      if (isFile(resource)) {
        moveFile({ file: resource, dirId: id });
      }

      if (isDir(resource)) {
        moveDir({
          id: resource.id,
          parent: id,
        });
      }
    });
  };

  return (
    <ClickAwayListener onClickAway={handleAwayClick}>
      <div onClick={handleWrapperClick}>
        <View
          resources={resources}
          onDeleteMany={handleDeleteMany}
          onClick={handleItemClick}
          onDoubleClick={handleItemDoubleClick}
          onItemRightClick={handleItemRightClick}
          {...selectionProps}
          {...rest}
        />
        <ContextMenuContainer
          open={isContextMenuOpen}
          onClose={closeContextMenu}
          resource={resource as Resource}
          x={x as number}
          y={y as number}
        />
        <TableEditBar
          selected={selectionProps.selectedItems}
          extraActions={() => (
            <>
              <Button onClick={addToBasketSelected}>
                <InboxIcon />
                {t('Add To Collection')}
              </Button>
              <Button onClick={downloadSelected}>
                <GetAppIcon />
                {t('Download')}
              </Button>
            </>
          )}
          onDelete={handleDeleteManyWithConfirm}
          onMove={handleMoveMany}
        />
      </div>
    </ClickAwayListener>
  );
};

export default memo(withConfirm<Props>(ResultsView), shallowEqual);
